/** 한샘 AS케어+ VO 는 파일이 분리돼 있다 — 패키지 루트에서 쓰려면 여기서 다시 내보내야 한다 */
import { YesOrNo } from '@store/base';

export * from './as-care';

export type LeftOrRight = 'Left' | 'Right';

/**
 * W:웹 / M:모웹 / I:IOS / A:안드로이드
 */
export type DeviceChannel = 'W' | 'M' | 'I' | 'A';

/**
 * N:일반 / C:커튼 / F:패브릭윈도우
 */
export type OrderGoodsKindCode = 'N' | 'C' | 'F';

/**
 * addition : 추가상품 / main : 그 외 모두
 */
export type OrderGoodsArea = 'main' | 'addition';

/**
 * N:정상, P:일시중지, E:영구정지, R:판매대기, SD:일시품절, SH:품절
 */
export type GoodsStatusCode = 'N' | 'P' | 'E' | 'R' | 'SD' | 'SH' | 'X1';

export type ConsultType = 'VISIT_SHOP' | 'HF_VISIT_RESERVED' | 'STORAGE_UNTACT';

export type ConsultTypeNm = '직접방문' | '방문상담예약' | '수납설계제안';

// 패브릭 상품 종류 constant추가
export const FAB_CURTAIN = 'CT'; // 커튼
export const FAB_BLIND = 'BL'; // 블라인드
export const FAB_RUG = 'RG'; // 러그
export const FAB_STANDARD = 'STD'; // 규격상품
export const FAB_ACCESSORY = 'ACC'; // ACC/부자재
export const BL_SINGLE_CODE = 'FBW_S'; // 블라인드 - 단창 코드

/**
 * 주문 체크룰 안내 유형 (BuyResponse.Notice.type)
 * VISIT_FEE_REQUIRED: 출장비 누락 / VISIT_FEE_DUPLICATE: 출장비 초과
 */
export type OrderNoticeType = 'VISIT_FEE_REQUIRED' | 'VISIT_FEE_DUPLICATE';

/**
 * 주문 요청 응답에 함께 실려오는 체크룰 안내 (없으면 null)
 * 응답 code 는 정상과 동일하게 200 이며, 안내 유형은 notice.type 으로 구분한다.
 */
export interface OrderNotice {
  type: OrderNoticeType;
  message: string;
}

interface BaseOrderGoods {
  gdsKindCd: OrderGoodsKindCode;
  gdsArea: OrderGoodsArea;
  gdsNo: number;
  unitNo: number;
  qty: number;
}

export interface OrderGoodsNormal extends BaseOrderGoods {
  gdsKindCd: 'N';
}

export interface OrderGoodsCurtain extends BaseOrderGoods {
  gdsKindCd: 'C';
  curtainCalcText?: string;
}

export interface OrderGoodsFabricWindow extends BaseOrderGoods {
  gdsKindCd: 'F';
  livingFabricAmount?: string;
  livingMarginYN?: YesOrNo;
  livingAllHeight?: number;
  livingAllWidth?: number;
  livingLeftWidth?: number;
  livingMiddleWidth?: number;
  livingRightWidth?: number;
  livingLeftHandle?: LeftOrRight;
  livingMiddleHandle?: LeftOrRight;
  livingRightHandle?: LeftOrRight;
  bedroomAllHeight?: number;
  bedroomAllWidth?: number;
  bedroomMarginYN?: YesOrNo;
  room1FabricAmount?: string;
  room1MarginYN?: YesOrNo;
  room1AllHeight?: number;
  room1AllWidth?: number;
  room1LeftWidth?: number;
  room1RightWidth?: number;
  room1AllHandle?: LeftOrRight;
  room1LeftHandle?: LeftOrRight;
  room1RightHandle?: LeftOrRight;
  room2FabricAmount?: string;
  room2MarginYN?: YesOrNo;
  room2AllHeight?: number;
  room2AllWidth?: number;
  room2LeftWidth?: number;
  room2RightWidth?: number;
  room2AllHandle?: LeftOrRight;
  room2LeftHandle?: LeftOrRight;
  room2RightHandle?: LeftOrRight;
}

export interface OrderGoodsCustom extends BaseOrderGoods {
  gdsKindCd: 'N';
  gdsArea: 'main';
  gdsCustomNo?: number;
  orgGdsCustomNo?: number;
}

/**
 * 회원 , 비회원 온라인견적서 목록
 */
export interface Estimates {
  estimateId: number;
  gdsSummary: string;
  estimateValidYn: boolean;
  totalOrderPrc: number;
  version: number; // 1: 구견적, 2: 신견적
}

export interface OnlineEstimate {
  consultId: number;
  consultType: ConsultType;
  consultTypeNm: ConsultTypeNm;
  consultDate: string;
  saleShopName: string;
  estimates: Estimates[];
}

// 결제쿠폰 적용 순번
export interface AppliedSequences {
  estimateGdsId: number;
  appliedGoodsQty: number;
}

export interface PayCoupon {
  promoTypCd: string;
  promoNo: number;
  promoNm: string;
  cponNo: number;
  rateOrAmtTypCd: string;
  applyAmtPct: number;
  totalApplyAmt: number;
  useAbleBgnYmdt: string;
  useAbleEndYmdt: string;
  appliedSequences: AppliedSequences[];
}

/**
 * OFFBEN-83 결제할인 분리 — 결제쿠폰 적용 단계 (상세 v3, version=2).
 * 실응답 확인: appliedCoupon 은 PayCoupon 에서 appliedSequences 제외(Ver2 응답엔 미포함).
 * version / payCoupons 는 Estimates · OnlineEstimateDetail 에 흡수.
 */
export interface PayCouponVer2 {
  payCouponStep: number; // 1~4 적용 단계
  appliedCoupon: Omit<PayCoupon, 'appliedSequences'>;
}

export interface Estimate {
  estimateId: number;
  estimateSeq: number;
  comment: string;
  delYn: boolean;
  transferYn: boolean;
  transferYmdt: string;
  estimateValidDate: string;
  ordRequestYn: boolean;
  ordRequestYmdt: string;
  ordYn: boolean;
  ordNo: string;
  custNo: string;
  completeConsultYn: boolean;
  modYmdt: string;
  gdsCustomYn: boolean;
  consultId: number;
}

export interface OrderBenefit {
  maxPromoApplyYn: boolean;
  totalNormalSalePrc: number;
  totalDcAmt: number;
  totalCouponAmt: number;
  applyCouponCount: number;
  totalPayCouponAmt: number;
  applyPayCouponCount: number;
  totalBenefitAmt: number;
  totalOrderPrc: number;
  graPercent: number;
  graAmt: number;
  usableCouponYn: string;
  usablePayCouponYn: string;
  downloadCouponYn: string;
  useAbleEndYmdt: string;
}

export type ReferenceImages = {
  estimateImageId: number;
  imgSeq: number;
  imgType: string;
  imgUrl: string;
  imgPath: string;
  imgOrgFileNm: string;
};

export interface AppliedBenefits {
  promoTypCd: string;
  promoNo: number;
  promoNm: string;
  cponNo: number;
  rateOrAmtTypCd: string;
  applyAmtPct: number;
  perApplyAmt: number;
  totalApplyAmt: number;
  oneQtyApplyYn: boolean;
  useAbleBgnYmdt: string;
  useAbleEndYmdt: string;
}

/**
 * 옵션유형 코드 (CT: 커튼, BL: 블라인드, RG: 러그, STD: 규격품, ACC: 부자재
 */
export type FabricOptionTypeCdCode = 'CT' | 'BL' | 'RG' | 'STD' | 'ACC';

export interface CustomGoodsFabricInfo {
  /** 커튼 제작 단위 */
  fbCMakeUnitCd: string | null;
  /** 커튼 제작 단위명 */
  fbCMakeUnitNm: string | null;
  /** 커튼 주름배수 */
  fbCCreaseMltplCd: string | null;
  /** 커튼 주름배수 명 */
  fbCCreaseMltplNm: string | null;
  /** 커튼 폭 수 */
  fbCWidthNum: number | null;
  /** 커튼 소요량 */
  fbCRqrmnt: number | null;
  /** 커튼 주름 가공 유형 */
  fbCCreaseMnfctrCd: string | null;
  /** 커튼 주름 가공 명 */
  fbCCreaseMnfctrNm: string | null;
  /** 커튼 겹 가공 유형 */
  fbCLayerMnfctrCd: string | null;
  /** 커튼 겹 가공 명 */
  fbCLayerMnfctrNm: string | null;
  /** 커튼 옵션 가공 유형 */
  fbCOptMnfctrCd: string | null;
  /** 커튼 옵션 가공 명 */
  fbCOptMnfctrNm: string | null;
  /** 블라인드 옵션 */
  fbBOptCd: string | null;
  /** 블라인드 옵션명 */
  fbBOptNm: string | null;
  /** 블라인드 손잡이 방향 */
  fbBHandleDirectCd: string | null;
  /** 블라인드 손잡이 방향명 */
  fbBHandleDirectNm: string | null;
  /** 블라인드 단독/연창 구분 */
  fbBCntnsTypeCd: string | null;
  /** 블라인드 단독/연창 구분명 */
  fbBCntnsTypeNm: string | null;
  /** 블라인드 연창 순서 */
  fbBCntnsSeq: number | null;
  /** 러그 형태 유형 */
  fbLFormCd: string | null;
  /** 러그 형태 유형명 */
  fbLFormNm: string | null;
  /** 블라인드 면적 */
  fbArea: number | null;
  /** 러그 면적 */
  fbAreaRug: number | null;
  /** 공간별 공통코드 */
  installRoomCd: string | null;
  /** 비고 */
  remark: string | null;
  /** 입고가 */
  fbGrPrc: number;
  /** 물대 공급가 */
  fbFactPrc: number;
  /** 물대 소비자가 */
  fbCustPrc: number;
  /** 시공비 공급가 */
  fbBuldFactPrc: number;
  /** 시공비 소비자가 */
  fbBuldCustPrc: number;
  /** 총 공급가 */
  fbTotalFactPrc: number;
  /** 총 소비자가 */
  fbTotalCustPrc: number;
  /** 옵션번호 */
  unitNo: number;
  /** 옵션유형코드 */
  fbOptTypeCd: FabricOptionTypeCdCode;
  /** 옵션유형명 */
  fbOptTypeNm: string;
  /** 패브릭 시공 여부 */
  fbBuildYn: boolean;
}

export interface OrderLines {
  estimateGdsId: number;
  estimateGdsSeq: number;
  imgUrl: string;
  gdsNo: number;
  gdsNm: string;
  unitNo: number;
  unitNm: string;
  gdsCustomNo: number;
  customGoodsType: string;
  qty: number;
  brandNm: string;
  brandNo: number;
  buyAbleYn: boolean;
  gdsStatCd: GoodsStatusCode;
  gdsStatNm: string;
  perUnitPrc: number;
  perGdsPrc: number;
  perUnitAddPrc: number;
  perCouponPrc: number;
  normalSalePrc: number;
  dcPrc: number;
  couponPrc: number;
  appliedBenefits: AppliedBenefits[];

  // 패브릭 커스텀 상품 response 추가
  logiUnits?: {
    logiUnitCd: string;
    logiUnitNm: string;
    logiUnitBaseQty: number;
    logiUnitQty: number;
    procCd: string;
    installRoomCd?: string;
    installRoomNm?: string;
    omFabricCalculatorYn?: boolean;
    omFabricYn?: boolean;
    customGoodsFabric?: CustomGoodsFabricInfo;
  }[];
  customGoodsInfo?: {
    srcCd?: string;
    srcCdId?: string;
    customGoodsType?: string;
  };
}

export type ButtonTypes = {
  buttonType: string;
  buttonTitle: string;
};

export interface EmployeeInfo {
  agentEmployeeName: string;
  agentEmployeeTel: string;
  assignEmployeeThumbnail: string;
  shopCode: string;
  shopName: string;
  saleChannelCode: string;
}

/**
 * 회원 / 비회원 온라인견적서 내용 조회
 */

export interface OnlineEstimateDetail {
  buttonTypes: ButtonTypes[];
  employee: EmployeeInfo;
  estimate: Estimate;
  orderBenefit: OrderBenefit;
  orderLines: OrderLines[];
  payCoupon?: PayCoupon; // 단수 (version=1; version=2엔 없을 수 있음)
  referenceImages: ReferenceImages[];
  version: number; // 1: 구견적, 2: 신견적
  payCoupons?: PayCouponVer2[]; // 복수/단계별 (version=2)
}

/**
 * 견적 알림톡 내 참고이미지 (HSMALL-15407)
 * ReferenceImages 와 달리 imgType 미포함 — BE 응답 스펙(HSMALL-15366) 기준
 */
export type EstimateNotificationImage = {
  estimateImageId: number;
  imgSeq: number;
  imgUrl: string;
  imgPath: string;
  imgOrgFileNm: string;
};

/**
 * 소재고지 알림톡 링크 고지내역 (SALES-14036 / 기획 SALES-13600)
 * 발송 시점의 이미지 URL 을 PC/MO 로 나눠 내려준다 — 클라이언트가 채널에 맞는 쪽을 고른다.
 */
export type EstimateMaterialNotice = {
  sendId: number; // 발송 이력 ID (리스트 key)
  sendYmdt: string; // 발송 일시 `yyyy-MM-dd HH:mm:ss`
  imgUrlPc: string;
  imgUrlMo: string;
};
